import * as React from 'react';
import { UIText2 } from '@cian/ui-kit';
import { IconFunctionalSuccess16 } from '@cian/ui-kit-design-tokens/icons';
import { clsx } from 'clsx';

import * as styles from './SortingDesktopDropdown.css';
import { ISortingProps } from '../../types';

export interface ISortingDesktopDropdownProps extends ISortingProps {
  dropdownRef: React.RefObject<HTMLUListElement>;
  onClose(): void;
}

export const SortingDesktopDropdown: React.FC<ISortingDesktopDropdownProps> = ({
  dropdownRef,
  onChange,
  onClose,
  options,
  selectedOption,
}) => {
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  const focusedRef = React.useRef<HTMLLIElement>(null);
  const isFirstRender = React.useRef<boolean>(true);

  const selectOption = React.useCallback(
    (index: number) => {
      const nextOption = options[index];

      if (!selectedOption || nextOption.value !== selectedOption.value) {
        onChange(nextOption);
      }

      onClose();
    },
    [onChange, onClose, selectedOption, options],
  );

  const selectFocusedOption = React.useCallback(() => {
    selectOption(focusedIndex);
  }, [focusedIndex, selectOption]);

  const handleClickOption = React.useCallback(
    (event: React.MouseEvent) => {
      const index = Number(event.currentTarget.getAttribute('data-index'));

      selectOption(index);
    },
    [selectOption],
  );

  const handleMouseMove = React.useCallback((event: React.MouseEvent) => {
    const index = Number(event.currentTarget.getAttribute('data-index'));
    setFocusedIndex(index);
  }, []);

  const updateFocusedIndex = React.useCallback(
    (direction: -1 | 1) => {
      setFocusedIndex(prev => {
        const nextIndex = prev + direction;

        if (nextIndex >= options.length) {
          return 0;
        }

        if (nextIndex < 0) {
          return options.length - 1;
        }

        return nextIndex;
      });
    },
    [options.length],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.code) {
        case 'Tab':
        case 'Escape':
          event.preventDefault();
          onClose();
          break;

        case 'Enter':
        case 'Space':
          event.preventDefault();
          selectFocusedOption();
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          event.preventDefault();
          updateFocusedIndex(event.code === 'ArrowUp' ? -1 : 1);
          break;
      }
    },
    [onClose, selectFocusedOption, updateFocusedIndex],
  );

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      if (selectedOption) {
        setFocusedIndex(options.findIndex(({ value }) => selectedOption.value === value));
      }

      if (dropdownRef.current) {
        dropdownRef.current.focus();
      }
    } else {
      if (focusedRef.current) {
        focusedRef.current.focus();
      }
    }
    // oxlint-disable-line react/exhaustive-deps
  }, [focusedIndex]);

  return (
    <ul ref={dropdownRef} className={styles['options-list']} role="listbox" tabIndex={-1} onKeyDown={handleKeyDown}>
      {options.map((option, idx) => {
        const isSelected = (selectedOption && option.value === selectedOption.value) || false;
        const isFocused = idx === focusedIndex;

        return (
          <li
            ref={isFocused ? focusedRef : null}
            className={clsx(styles['option'], idx === focusedIndex && styles['focus'])}
            role="option"
            aria-checked={isSelected}
            key={option.value}
            data-index={idx}
            data-value={option.value}
            onClick={handleClickOption}
            onMouseMove={handleMouseMove}
          >
            <UIText2>{option.label}</UIText2>
            <span className={styles['check']}>
              {isSelected && <IconFunctionalSuccess16 color="icon-main-default" />}
            </span>
          </li>
        );
      })}
    </ul>
  );
};
